import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

interface FreeTrialFadeProps {
  children: React.ReactNode;
  enabled?: boolean;
  className?: string;
  message?: string;
  unlockTitle?: string;
  alignTop?: boolean;
}

const FreeTrialFade: React.FC<FreeTrialFadeProps> = ({
  children,
  enabled = true,
  className = '',
  message = 'Subscribe here to view this chart',
  unlockTitle = 'Unlock this chart',
  alignTop = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const isPaidSubscriber = (user?.subscription_status || '').toLowerCase() === 'active';
  const isFreeTrial = !!user?.on_free_trial && !isPaidSubscriber;
  const shouldGate = enabled && isFreeTrial;

  if (!shouldGate) return <>{children}</>;

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Blurred chart underneath */}
      <div className="blur-md pointer-events-none select-none w-full h-full flex flex-col">
        {children}
      </div>

      {/* Lock + message overlay - fades out on hover */}
      <div
        className={`absolute inset-0 z-10 flex flex-col items-center px-4 rounded backdrop-blur-sm transition-opacity duration-500 ${
          alignTop ? 'justify-start' : 'justify-center'
        } ${isDark ? 'bg-rs-black/50' : 'bg-rs-off-white/40'} ${
          isHovered ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className={`flex flex-col items-center gap-2 text-center ${alignTop ? 'sticky top-32' : ''}`}>
          <Lock className="w-8 h-8 text-rs-green" />
          <p className={`text-sm font-semibold ${isDark ? 'text-rs-off-white' : 'text-black'}`}>
            {message}
          </p>
        </div>
      </div>

      {/* Subscribe overlay - fades in on hover */}
      <div
        className={`absolute inset-0 z-20 flex justify-center rounded backdrop-blur-sm transition-opacity duration-500 ${
          alignTop ? 'items-start' : 'items-center'
        } ${isDark ? 'bg-rs-black/90' : 'bg-rs-off-white/40'} ${
          isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className={`flex flex-col items-center gap-4 text-center px-6 ${alignTop ? 'sticky top-32' : ''}`}>
          <Lock className="w-12 h-12 text-rs-green" />
          <div>
            <p className={`font-semibold text-lg mb-2 ${isDark ? 'text-rs-off-white' : 'text-black'}`}>
              {unlockTitle}
            </p>
            <p className={`text-sm mb-4 ${isDark ? 'text-rs-off-white/60' : 'text-black/60'}`}>
              Subscribe to access all premium features
            </p>
          </div>
          <button
            onClick={() => navigate('/billing')}
            className="px-6 py-2.5 bg-rs-green text-black font-semibold rounded-lg hover:bg-rs-dark-green transition-colors"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeTrialFade;
